import { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { useLocalizedPath } from '#app/utils/i18n.ts'
import { cn } from '#app/utils/misc.tsx'
import { Alert, AlertDescription } from './ui/alert.tsx'
import { FieldError } from './ui/field.tsx'
import { Input } from './ui/input.tsx'
import { StatusButton } from './ui/status-button.tsx'

type ActionData =
	| { ok: true; error: null }
	| { ok: false; error: 'invalid_email' | 'generic' }

export function NewsletterSignup({ compact = false }: { compact?: boolean }) {
	const { t } = useTranslation()
	const localePath = useLocalizedPath()
	const fetcher = useFetcher<ActionData>()
	const isSubmitting = fetcher.state !== 'idle'
	const isSuccess = fetcher.data?.ok === true
	const errorType = fetcher.data?.ok === false ? fetcher.data.error : null

	const buttonRef = useRef<HTMLButtonElement>(null)
	// Cached origin so the confetti can still fire after the button unmounts
	// (the success branch replaces the form when isSuccess flips true).
	const lastOriginRef = useRef<{ x: number; y: number } | null>(null)

	useEffect(() => {
		if (isSuccess || typeof window === 'undefined') return
		const node = buttonRef.current
		if (!node) return
		const rect = node.getBoundingClientRect()
		lastOriginRef.current = {
			x: (rect.left + rect.width / 2) / window.innerWidth,
			y: (rect.top + rect.height / 2) / window.innerHeight,
		}
	})

	useEffect(() => {
		if (!isSuccess || typeof window === 'undefined') return
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
		const origin = lastOriginRef.current
		if (!origin) return
		let cancelled = false
		void import('canvas-confetti').then(({ default: confetti }) => {
			if (cancelled) return
			void confetti({
				origin,
				particleCount: 80,
				spread: 60,
				startVelocity: 35,
				scalar: 0.8,
			})
		})
		return () => {
			cancelled = true
		}
	}, [isSuccess])

	if (isSuccess) {
		return (
			<div className={cn(compact ? 'py-2' : 'py-6')}>
				<h3 className="text-body-md text-foreground font-semibold">
					{t('newsletter.successHeading')}
				</h3>
				<p className="text-body-xs text-muted-foreground mt-2">
					{t('newsletter.successMessage')}
				</p>
			</div>
		)
	}

	return (
		<div className={cn(compact ? 'py-2' : 'py-6')}>
			<h3 className="text-body-md text-foreground font-semibold">
				{t('newsletter.heading')}
			</h3>
			<p className="text-body-xs text-muted-foreground mt-2">
				{t('newsletter.description')}
			</p>
			<fetcher.Form
				method="POST"
				action="/resources/newsletter"
				className="mt-4 flex flex-col gap-2 sm:flex-row"
			>
				<HoneypotInputs />
				<label htmlFor="newsletter-email" className="sr-only">
					{t('newsletter.emailLabel')}
				</label>
				<Input
					id="newsletter-email"
					type="email"
					name="email"
					placeholder={t('newsletter.placeholder')}
					required
					aria-invalid={errorType ? true : undefined}
					aria-describedby={errorType ? 'newsletter-error' : undefined}
					className="flex-1"
				/>
				<StatusButton
					ref={buttonRef}
					type="submit"
					status={isSubmitting ? 'pending' : 'idle'}
					disabled={isSubmitting}
				>
					{isSubmitting
						? t('newsletter.submittingLabel')
						: t('newsletter.submitLabel')}
				</StatusButton>
			</fetcher.Form>
			{errorType === 'invalid_email' ? (
				<FieldError id="newsletter-error" className="mt-2">
					{t('newsletter.errors.invalidEmail')}
				</FieldError>
			) : errorType ? (
				<Alert id="newsletter-error" tone="error" className="mt-2">
					<AlertDescription>{t('newsletter.errors.generic')}</AlertDescription>
				</Alert>
			) : null}
			<p className="text-body-2xs text-muted-foreground mt-3">
				<Trans
					i18nKey="newsletter.privacyNote"
					components={{
						1: (
							<Link
								to={localePath('/privacy')}
								className="link-inline focus-cosy"
							/>
						),
					}}
				/>
			</p>
		</div>
	)
}
